import { useCallback, useEffect, useMemo } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { getTotalStudentsFiltered } from '../graphQLOperations/Dashboard';
import { DashboardGetStudentsCountQuery } from '../graphQLOperations/__generated__/DashboardGetStudentsCountQuery.graphql';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';
import { selectSelectedSiteIds, setSelectedSiteIds } from '../redux/reducers/globalClassFilterSlice';
import { readStoredIds, writeStoredIds } from '../businessLogicService/globalClassFilterStorage';

export interface GlobalFilterSite {
  id: string;
  name: string;
}

export interface GlobalSiteFilter {
  sites: GlobalFilterSite[];
  selectedSites: GlobalFilterSite[];
  selectedStudentCount: number;
  select: (siteIds: string[]) => void;
}

export const useGlobalSiteFilter = (): GlobalSiteFilter => {
  const dispatch = useAppDispatch();
  const userRedux = useAppSelector(selectUser);
  const publicUserId = userRedux?.sub ?? '';
  const selectedSiteIds = useAppSelector(selectSelectedSiteIds);

  const sites = useMemo<GlobalFilterSite[]>(
    () =>
      [...(userRedux?.sites ?? [])]
        .map(site => ({ id: site.id ?? '', name: site.name ?? '' }))
        .filter(site => site.id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [userRedux?.sites],
  );

  useEffect(() => {
    if (!publicUserId || selectedSiteIds.length) return;

    const available = new Set(sites.map(site => site.id));
    const stored = readStoredIds('sites', publicUserId).filter(id => available.has(id));

    const restored = stored.length ? stored : sites.slice(0, 1).map(site => site.id);
    if (restored.length) dispatch(setSelectedSiteIds(restored));
  }, [publicUserId, sites, selectedSiteIds.length, dispatch]);

  const selectedSites = useMemo(() => {
    const selected = new Set(selectedSiteIds);
    return sites.filter(site => selected.has(site.id));
  }, [sites, selectedSiteIds]);

  const { allStudents } = useLazyLoadQuery<DashboardGetStudentsCountQuery>(
    getTotalStudentsFiltered,
    {
      skip: !selectedSites.length,
      filter: { siteIds: selectedSites.length ? selectedSites.map(site => site.id) : null },
    },
    { fetchPolicy: 'store-or-network' },
  );

  const select = useCallback(
    (siteIds: string[]) => {
      if (!siteIds.length) return;
      dispatch(setSelectedSiteIds(siteIds));
      writeStoredIds('sites', publicUserId, siteIds);
    },
    [dispatch, publicUserId],
  );

  return { sites, selectedSites, selectedStudentCount: allStudents?.totalCount ?? 0, select };
};
